// [2026-09-03] ADDED — the Team screen: who at this company can open the dashboard.
//
// WHY THIS SCREEN EXISTS AT ALL. The account session identifies a COMPANY, so before members every
// sub-user of a customer could read every candidate's recording, mint API keys and spend credits.
// The backend now refuses anyone without a member record; this is the only place those records can
// be created, so without this screen the lock is a door with no handle.
//
// WHAT IS AND IS NOT A SECURITY CONTROL HERE. Nothing on this page authorises anything. `my_role`
// hides buttons a viewer cannot use, which is politeness — it stops us showing a control that would
// answer 403. Every action is authorised again server-side in app/modules/me/route.py. Anyone
// reading this file looking for the permission check should look there.
//
// [2026-09-04] CHANGED — rebuilt to match the InCruiter account app's screen at
// /roles-subuser-list (frontend-account/src/container/RolesAndSubUser/RolesAndSubUsersList.jsx):
// Subuser | Roles tabs, the light-blue column bar, white row cards, the OUTLINED status pill, the
// three-dot action menu and the "No Record Found" illustration. A customer who manages sub-users in
// the account app should not have to learn a second screen here.
//
// EVERY MUTATION IS UNCHANGED. invite / changeRole / revoke and their confirmation strings are the
// same code as before; only the presentation is new. The previous render block is kept at the
// bottom of this file, commented out and date-stamped, per the repo rule.
//
// WHAT THE ACCOUNT APP HAS THAT THIS DELIBERATELY DOES NOT:
//   * "Add Role" — there is no role-creation endpoint. Our roles are a fixed three-value enum
//     (app/core/members.py:59-62). A button that opened a form the server cannot accept is exactly
//     the "flag that does nothing" the repo rules forbid.
//   * Editable permissions — same reason. The Permissions link opens a READ-ONLY view of what the
//     server actually enforces.
//   * Sortable column headers — GET /me/members takes no sort parameter, and a header that sorted
//     only the rows already downloaded would look like it ordered the list and silently not.

import { useCallback, useEffect, useRef, useState } from 'react'
import { platform } from '../../api.js'
import { toast } from '../../ui.jsx'
import { PfModal, PfNotice, PfSkeleton } from '../../components/platform/ui.jsx'
import { DownloadIcon, PlusIcon, SearchIcon } from '../../components/platform/icons.jsx'
import noDataImg from '../../assets/no-data-found.svg'
import threeDotImg from '../../assets/threedot.svg'
import { downloadCsv } from '../../ui.jsx'
// [2026-09-05] ADDED — replaces window.confirm(); see components/platform/confirm.jsx.
import { useConfirm } from '../../components/platform/confirm.jsx'

const ROLE_LABEL = {
  owner: 'Owner',
  admin: 'Admin',
  viewer: 'Viewer',
}

// Spelled out rather than left to the reader, because "admin" and "viewer" sound similar and the
// difference is who can spend money and read candidate recordings.
const ROLE_HELP = {
  owner: 'Full access. Cannot be removed while they are the only owner.',
  admin: 'Everything a viewer can do, plus API keys and managing this list.',
  viewer: 'Reads reports, analytics and the credit balance. Changes nothing.',
}

// [2026-09-04] ADDED — the Roles tab.
//
// These are NOT fetched: app/core/members.py:59-62 defines ROLES as a fixed list, and there is no
// endpoint that returns or edits them. Rendering them from a constant is the truthful thing to do;
// inventing a GET /me/roles that does not exist would be worse.
//
// `code` mirrors the account app's #ROLE0000n display id. Ordered strongest-first, as that screen
// orders them.
const ROLE_ROWS = [
  { key: 'owner',  code: '#ROLE00001', name: 'Owner' },
  { key: 'admin',  code: '#ROLE00002', name: 'Admin' },
  { key: 'viewer', code: '#ROLE00003', name: 'Viewer' },
]

// The capability matrix, read out of app/modules/me/route.py on 2026-09-04 — what the server
// ENFORCES, not what any comment claims. See the note rendered under the table about API keys.
const CAPABILITIES = [
  { label: 'View the dashboard, proctoring reports and analytics', viewer: true, admin: true, owner: true },
  { label: 'View the credit balance and ledger',                   viewer: true, admin: true, owner: true },
  { label: 'Download evidence screenshots',                        viewer: true, admin: true, owner: true },
  { label: 'Create, rotate and revoke API keys',                   viewer: true, admin: true, owner: true },
  { label: 'Invite people, change roles and remove access',        viewer: false, admin: true, owner: true },
  { label: 'Cannot be removed while the last owner',               viewer: false, admin: false, owner: true },
]

const STATUS_TONE = { active: 'on', invited: 'idle', revoked: 'off' }
const STATUS_LABEL = { active: 'Active', invited: 'Invited', revoked: 'Removed' }

const SUBUSER_COLS = '48px minmax(140px,1.1fr) minmax(200px,1.5fr) 110px 150px 70px'
const ROLE_COLS = 'minmax(120px,1fr) minmax(140px,1fr) 150px 130px 70px'

function when(value) {
  if (!value) return '—'
  // The API sends epoch SECONDS here (time.time()), not the ISO strings the session tables use.
  const date = new Date(typeof value === 'number' ? value * 1000 : value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** The account app's outlined status pill. Always renders the WORD — the colour is secondary. */
function StatusPill({ status }) {
  return (
    <span className={`pf-status-outline ${STATUS_TONE[status] || 'idle'}`}>
      {STATUS_LABEL[status] || status}
    </span>
  )
}

/** The three-dot action menu, matching the account app's MenuButton.
 *
 *  Renders NOTHING when it has no enabled option, rather than a button that opens an empty box —
 *  which is what a viewer would otherwise get on every row. */
function MenuButton({ options }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!options.length) return null

  return (
    <div className="pf-kebab-wrap" ref={ref}>
      <button className="pf-kebab" aria-haspopup="menu" aria-expanded={open}
              aria-label="Actions" onClick={() => setOpen((v) => !v)}>
        <img src={threeDotImg} alt="" aria-hidden="true" />
      </button>
      {open && (
        <div className="pf-kebab-menu" role="menu">
          {options.map((o) => (
            <button key={o.name} role="menuitem" disabled={o.disabled}
                    className={o.danger ? 'danger' : undefined}
                    onClick={() => { setOpen(false); o.action() }}>
              {o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function NoRecordFound() {
  return (
    <div className="pf-nodata">
      <img src={noDataImg} alt="" aria-hidden="true" />
      <strong>No Record Found</strong>
    </div>
  )
}

export default function PlatformMembers() {
  const [state, setState] = useState({ loading: true, rows: [], myRole: 'viewer', myEmail: '' })
  const [tab, setTab] = useState('subuser')
  const [search, setSearch] = useState('')
  const [limit, setLimit] = useState(10)
  const [inviting, setInviting] = useState(false)
  const [permissionsFor, setPermissionsFor] = useState(null)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('viewer')
  const [busy, setBusy] = useState(false)
  // Shown ONLY when the invitation email could not be delivered, so an admin still has a way to get
  // the person in. See the route: accept_url is null on the normal path, so a live token is never
  // sitting in a successful API response.
  const [fallbackLink, setFallbackLink] = useState('')
  const [confirm, confirmDialog] = useConfirm()

  const load = useCallback(async () => {
    try {
      const data = await platform.members()
      setState({
        loading: false,
        rows: data.members || [],
        myRole: data.my_role || 'viewer',
        myEmail: data.my_email || '',
      })
    } catch (err) {
      setState((s) => ({ ...s, loading: false }))
      toast(err.message || 'Could not load the team list.', 'error')
    }
  }, [])

  useEffect(() => { load() }, [load])

  const canManage = state.myRole === 'admin' || state.myRole === 'owner'

  // ── the four mutations, UNCHANGED from the pre-revamp version ────────────────

  async function invite(event) {
    event.preventDefault()
    if (busy) return
    const address = email.trim()
    if (!address) return
    setBusy(true)
    setFallbackLink('')
    try {
      const data = await platform.inviteMember(address, role)
      if (data.email_delivered) {
        toast(`Invitation sent to ${address}.`, 'success')
        setInviting(false)
      } else {
        // NOT reported as success. The invitation is real and the token works, but nobody has been
        // told — saying "sent" here is how an admin waits two days for a colleague who never got it.
        // The dialog stays OPEN in this branch, because the fallback link is the only copy of it.
        toast('Invitation created, but the email could not be sent. Share the link below.', 'error')
        setFallbackLink(data.accept_url || '')
      }
      setEmail('')
      await load()
    } catch (err) {
      toast(err.message || 'Could not send the invitation.', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function changeRole(memberEmail, nextRole) {
    try {
      await platform.setMemberRole(memberEmail, nextRole)
      toast(`${memberEmail} is now ${ROLE_LABEL[nextRole] || nextRole}.`, 'success')
      await load()
    } catch (err) {
      toast(err.message || 'Could not change that role.', 'error')
      await load()   // reload so the row snaps back to the truth, not the failed choice
    }
  }

  async function revoke(memberEmail) {
    // [2026-09-05] CHANGED — this WAS a deliberate blocking window.confirm, on the reasoning that
    // removing someone's access to candidate recordings is worth one unmissable extra click. That
    // reasoning still holds; the browser dialog was the wrong way to honour it. Our dialog focuses
    // Cancel, states the consequence in full, and cannot be suppressed into silently returning
    // false the way window.confirm can inside a sandboxed frame.
    // OLD: if (!window.confirm(`Remove ${memberEmail}'s access to this dashboard?`)) return
    if (!(await confirm({
      title: 'Remove access?',
      subtitle: memberEmail,
      message: 'They lose access to this dashboard immediately, including every candidate '
             + 'recording and screenshot. They stay on the list as removed, so the record of who '
             + 'had access stays auditable.',
      confirmLabel: 'Remove access',
    }))) return
    try {
      await platform.revokeMember(memberEmail)
      toast(`${memberEmail} no longer has access.`, 'success')
      await load()
    } catch (err) {
      toast(err.message || 'Could not remove that member.', 'error')
    }
  }

  if (state.loading) return <PfSkeleton height={280} />

  // Filtered CLIENT-SIDE, and that is correct here specifically: GET /me/members returns the whole
  // list in one response (there is no pagination on that endpoint), so the browser already holds
  // every row. This is the opposite of the reports/requests tables, where searching what was
  // downloaded would search an arbitrary slice.
  const term = search.trim().toLowerCase()
  const filtered = term
    ? state.rows.filter((m) => `${m.email} ${m.name || ''} ${m.role}`.toLowerCase().includes(term))
    : state.rows
  const visible = filtered.slice(0, limit)

  const roleRows = ROLE_ROWS.filter((r) => !term || r.name.toLowerCase().includes(term))

  const exportRows = () => {
    if (tab === 'role') {
      downloadCsv('roles.csv', ROLE_ROWS.map((r) => ({
        'Role ID': r.code, Role: `${r.name} ( Global )`, Status: 'Active',
      })))
      return
    }
    downloadCsv('subusers.csv', filtered.map((m) => ({
      Name: m.name || 'Not Available',
      Email: m.email,
      Role: ROLE_LABEL[m.role] || m.role,
      Status: STATUS_LABEL[m.status] || m.status,
      Invited: when(m.invited_at),
      Joined: when(m.accepted_at),
    })))
  }

  return (
    <>
      <div className="pf-tabs">
        <button className={`pf-tab${tab === 'subuser' ? ' active' : ''}`}
                onClick={() => { setTab('subuser'); setSearch('') }}>Subuser</button>
        <button className={`pf-tab${tab === 'role' ? ' active' : ''}`}
                onClick={() => { setTab('role'); setSearch('') }}>Roles</button>
      </div>

      <div className="pf-toolbar">
        {tab === 'subuser' && (
          <label className="pf-entries">
            Show
            <select className="pf-select" aria-label="Entries per page" value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}>
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            Entries
          </label>
        )}

        <div className="pf-toolbar-spacer" />

        <div className="pf-search" style={{ flex: '0 1 340px' }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search here..." aria-label="Search" />
          <SearchIcon />
        </div>

        {/* Rendered only for someone who can actually use it — the server answers 403 otherwise
            (require_role(ADMIN) at app/modules/me/route.py:581). */}
        {tab === 'subuser' && canManage && (
          <button className="pf-btn" onClick={() => { setFallbackLink(''); setInviting(true) }}>
            <PlusIcon />Invite Users
          </button>
        )}
        {/* NO "Add Role" button on the Roles tab — see the header note. */}

        <button className="pf-btn ghost" onClick={exportRows}>
          <DownloadIcon />Export
        </button>
      </div>

      {tab === 'subuser' ? (
        <div className="pf-listscroll">
          <div>
            {visible.length > 0 && (
              <div className="pf-listhead" style={{ gridTemplateColumns: SUBUSER_COLS }}>
                <div>#</div><div>Name</div><div>Email</div><div>Role</div>
                <div>Status</div><div style={{ textAlign: 'center' }}>Action</div>
              </div>
            )}
            {visible.map((m, index) => {
              const isMe = m.email === state.myEmail
              const locked = !canManage || isMe || m.status === 'revoked'
              // Your OWN role is never editable here. Demoting yourself by accident is a way to lock
              // a company out of its own key management, and the server refuses to remove the last
              // owner anyway — this keeps the UI honest about what it will accept.
              const options = locked ? [] : [
                ...['viewer', 'admin', 'owner']
                  .filter((r) => r !== m.role)
                  .map((r) => ({ name: `Make ${ROLE_LABEL[r]}`, action: () => changeRole(m.email, r) })),
                { name: 'Remove access', danger: true, action: () => revoke(m.email) },
              ]
              return (
                <div className="pf-listrow" key={m.member_id || m.email}
                     style={{ gridTemplateColumns: SUBUSER_COLS }}>
                  <div className="pf-muted">{index + 1}</div>
                  <div className="pf-ellipsis" title={m.name || ''}>
                    {m.name || <span className="pf-muted">—</span>}
                    {isMe && <span className="pf-muted" style={{ marginLeft: 6, fontSize: 12 }}>(you)</span>}
                  </div>
                  <div className="pf-ellipsis" title={m.email}>{m.email}</div>
                  <div>{ROLE_LABEL[m.role] || m.role}</div>
                  <div><StatusPill status={m.status} /></div>
                  <div style={{ textAlign: 'center' }}><MenuButton options={options} /></div>
                </div>
              )
            })}
            {visible.length === 0 && <NoRecordFound />}
          </div>
        </div>
      ) : (
        <div className="pf-listscroll">
          <div>
            {roleRows.length > 0 && (
              <div className="pf-listhead" style={{ gridTemplateColumns: ROLE_COLS }}>
                <div>Role ID</div><div>Role</div>
                <div style={{ textAlign: 'center' }}>Status</div>
                <div style={{ textAlign: 'center' }}>Permissions</div>
                <div style={{ textAlign: 'center' }}>Action</div>
              </div>
            )}
            {roleRows.map((r) => (
              <div className="pf-listrow" key={r.key} style={{ gridTemplateColumns: ROLE_COLS }}>
                <div>{r.code}</div>
                <div>{r.name} ( Global )</div>
                <div style={{ textAlign: 'center' }}><StatusPill status="active" /></div>
                <div style={{ textAlign: 'center' }}>
                  <button className="pf-link" onClick={() => setPermissionsFor(r)}>Permissions</button>
                </div>
                <div style={{ textAlign: 'center' }}>
                  {/* One real option. The account app's Edit/Delete/Duplicate need endpoints this
                      service does not have, and a menu of disabled items is noise. */}
                  <MenuButton options={[
                    { name: 'View permissions', action: () => setPermissionsFor(r) },
                  ]} />
                </div>
              </div>
            ))}
            {roleRows.length === 0 && <NoRecordFound />}
          </div>
        </div>
      )}

      <div className="pf-copyright">© 2019 - 2026 <a href="https://incruiter.com">InCruiter</a></div>

      {/* ── invite ─────────────────────────────────────────────────────────── */}
      {inviting && (
        <PfModal title="Invite Users"
                 subtitle="They sign in with their existing InCruiter account — there is no new password."
                 onClose={() => setInviting(false)}>
          <form onSubmit={invite}>
            <label className="pf-field">
              <span>Work email</span>
              <input type="email" required autoFocus value={email} placeholder="colleague@company.com"
                     onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="pf-field">
              <span>Role</span>
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
                {/* Owner is absent on purpose: the API refuses to create one by invitation, so an
                    invite email can never mint a second owner. Ownership is granted afterwards,
                    from the list, by someone who already holds it. */}
              </select>
            </label>
            <p className="pf-muted" style={{ margin: 0, fontSize: 12.5 }}>
              {ROLE_HELP[role]} The invitation expires in 7 days and only works for this address.
            </p>

            {fallbackLink && (
              <div className="pf-modal-warn" style={{ marginTop: 16, marginBottom: 0 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Send this link to them yourself</div>
                <code style={{ wordBreak: 'break-all', fontSize: 12 }}>{fallbackLink}</code>
              </div>
            )}

            <div className="pf-modal-foot">
              <button type="button" className="pf-btn ghost"
                      onClick={() => setInviting(false)}>Cancel</button>
              <button type="submit" className="pf-btn" disabled={busy}>
                {busy ? 'Sending…' : 'Send invitation'}
              </button>
            </div>
          </form>
        </PfModal>
      )}

      {confirmDialog}

      {/* ── permissions (read-only) ────────────────────────────────────────── */}
      {permissionsFor && (
        <PfModal title={`${permissionsFor.name} — permissions`}
                 subtitle="What this role can do. Enforced by the server on every request."
                 onClose={() => setPermissionsFor(null)} wide>
          <div className="pf-table-wrap">
            <table className="pf-table">
              <thead>
                <tr>
                  <th>Capability</th>
                  {ROLE_ROWS.map((r) => (
                    <th key={r.key} className="num"
                        style={r.key === permissionsFor.key
                          ? { color: 'var(--pf-navy)', fontWeight: 700 } : undefined}>
                      {r.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CAPABILITIES.map((c) => (
                  <tr key={c.label}>
                    <td>{c.label}</td>
                    {ROLE_ROWS.map((r) => (
                      // The WORD, not a tick glyph — a column of ✓ and blank cells is unreadable to
                      // a screen reader and ambiguous when printed.
                      <td key={r.key} className="num"
                          style={{ color: c[r.key] ? 'var(--pf-good)' : 'var(--pf-ink-muted)',
                                   fontWeight: c[r.key] ? 600 : 400 }}>
                        {c[r.key] ? 'Yes' : 'No'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ⚠ This is a REAL FINDING, not a design note. app/modules/me/route.py calls
              require_role() on exactly three handlers — invite (:581), role change (:645) and
              revoke (:672). The API-key handlers (:207, :225, :260) call it on NONE of them, so a
              viewer can mint and revoke keys today. app/core/members.py:60 says admin means
              "+ API keys", so the code and its own comment disagree. The table above reports what
              is ENFORCED. Reported 2026-09-04; this note comes out when the backend is fixed. */}
          <PfNotice kind="warn">
            <strong>Known gap:</strong> API-key management is not role-gated on the server today —
            every member, including a viewer, can create and revoke keys. Raised with the backend
            on 2026-09-04. The table above shows what is actually enforced, not what is intended.
          </PfNotice>
        </PfModal>
      )}
    </>
  )
}

// [2026-09-04] --- OLD RENDER (pre-revamp), kept per the repo rule. ------------------------------
//
// The screen before it was matched to the account app: two ui.jsx <Card>s — an "Invite someone"
// form always visible at the top, and a plain <table> with an inline role <select> per row and a
// text "Remove" link. The mutations above are unchanged, so restoring this is a swap of the JSX.
//
//   <div style={{ display: 'grid', gap: 16 }}>
//     {canManage && (
//       <Card title="Invite someone"
//             blurb="They sign in with their existing InCruiter account — there is no new password.">
//         <form onSubmit={invite} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
//           <label style={{ display: 'grid', gap: 4, flex: '1 1 260px' }}>
//             <span style={{ fontSize: 12, color: 'var(--muted-fg, #6b7280)' }}>Work email</span>
//             <input type="email" required value={email} placeholder="colleague@company.com"
//                    onChange={(e) => setEmail(e.target.value)} />
//           </label>
//           <label style={{ display: 'grid', gap: 4, flex: '0 0 160px' }}>
//             <span style={{ fontSize: 12, color: 'var(--muted-fg, #6b7280)' }}>Role</span>
//             <select value={role} onChange={(e) => setRole(e.target.value)}>
//               <option value="viewer">Viewer</option>
//               <option value="admin">Admin</option>
//             </select>
//           </label>
//           <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send invitation'}</button>
//         </form>
//         <p>{ROLE_HELP[role]} The invitation expires in 7 days and only works for this address.</p>
//         {fallbackLink && (
//           <div><div>Send this link to them yourself</div><code>{fallbackLink}</code></div>
//         )}
//       </Card>
//     )}
//
//     <Card title="Team"
//           blurb="Everyone who can open this dashboard. Removed people are kept in the list so it stays auditable.">
//       <table>
//         <thead><tr><th>EMAIL</th><th>ROLE</th><th>STATUS</th><th>INVITED</th><th>JOINED</th>
//           {canManage && <th />}</tr></thead>
//         <tbody>
//           {state.rows.map((m) => {
//             const isMe = m.email === state.myEmail
//             return (
//               <tr key={m.member_id || m.email}>
//                 <td>{m.email}{isMe && <span>(you)</span>}</td>
//                 <td>
//                   {canManage && m.status !== 'revoked' && !isMe ? (
//                     <select value={m.role} onChange={(e) => changeRole(m.email, e.target.value)}>
//                       <option value="viewer">Viewer</option>
//                       <option value="admin">Admin</option>
//                       <option value="owner">Owner</option>
//                     </select>
//                   ) : (ROLE_LABEL[m.role] || m.role)}
//                 </td>
//                 <td><Pill status={m.status} /></td>
//                 <td>{when(m.invited_at)}</td>
//                 <td>{when(m.accepted_at)}</td>
//                 {canManage && (
//                   <td>{m.status !== 'revoked' && !isMe && (
//                     <button onClick={() => revoke(m.email)}>Remove</button>)}</td>
//                 )}
//               </tr>
//             )
//           })}
//           {state.rows.length === 0 && (
//             <tr><td colSpan={canManage ? 6 : 5}>Nobody else has been invited yet.</td></tr>
//           )}
//         </tbody>
//       </table>
//     </Card>
//   </div>
//
// --- END OLD RENDER -----------------------------------------------------------------------------
